import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact "⭐ Favorite Campaigns" strip for the Dashboard.
 * Renders horizontally-scrolling preview cards so users can jump straight
 * into their best-performing campaigns.
 *
 * Props:
 *  - sessionToken: string
 *  - colorTheme: 'green' | 'purple' | 'blue'
 *  - onOpenCampaign: (campaignId: string) => void
 */
const FavoriteCampaignsStrip = ({ sessionToken, colorTheme = 'green', onOpenCampaign }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState('');

  const fetchFavs = async () => {
    if (!sessionToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/favorites?limit=12`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
      });
      setItems(res.data?.items || []);
    } catch (_) {
      // Silent — dashboard shouldn't crash if favorites endpoint fails.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFavs(); /* eslint-disable-next-line */ }, [sessionToken]);

  const handleUnfavorite = async (e, campaignId) => {
    e.stopPropagation();
    if (removingId) return;
    setRemovingId(campaignId);
    // Optimistic — remove from strip immediately.
    setItems(prev => prev.filter(c => c.id !== campaignId));
    try {
      await axios.post(`${API}/campaigns/${campaignId}/favorite`,
        { is_favorite: false },
        { headers: { 'Authorization': `Bearer ${sessionToken}` } });
    } catch (err) {
      // Roll back on failure
      fetchFavs();
    } finally {
      setRemovingId('');
    }
  };

  if (loading || items.length === 0) return null;   // hide entirely when empty

  const accent =
    colorTheme === 'purple' ? 'text-purple-600 dark:text-purple-300'
    : colorTheme === 'blue' ? 'text-blue-600 dark:text-blue-300'
    : 'text-emerald-600 dark:text-emerald-300';

  return (
    <section className="mb-8" data-testid="dashboard-favorites-strip">
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          Favorite campaigns
        </h2>
        <span className={`text-xs font-medium ${accent}`}>{items.length}</span>
      </div>

      {/* Responsive layout: grid on mobile / tablet (no scroll needed), then
          horizontal scroll only when many favorites don't fit in a grid on
          desktop. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {items.slice(0, 12).map(c => (
          <div
            key={c.id}
            data-testid={`dashboard-favorite-card-${c.id.slice(0,8)}`}
            className="group relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
          >
            <button
              type="button"
              onClick={() => onOpenCampaign && onOpenCampaign(c.id)}
              className="w-full text-left"
            >
              <div className="aspect-video bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative overflow-hidden">
                {c.preview_url ? (
                  c.preview_kind === 'video' ? (
                    c.poster_url ? (
                      // Prefer a companion IMAGE ad from the same campaign
                      // as the poster — always renders instantly.
                      <img src={c.poster_url} alt={c.product_name || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    ) : (
                      <video
                        src={c.preview_url}
                        className="w-full h-full object-cover"
                        muted playsInline preload="auto"
                        onLoadedMetadata={(e) => { try { e.currentTarget.currentTime = 0.1; } catch (_) {} }}
                      />
                    )
                  ) : (
                    <img src={c.preview_url} alt={c.product_name || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl">📊</div>
                )}
                {c.preview_kind === 'video' && (
                  <>
                    <div className="absolute bottom-1.5 left-1.5 text-[10px] font-bold text-white px-1.5 py-0.5 rounded bg-red-500/90">VIDEO</div>
                    {/* Play glyph overlays regardless of poster/video mode */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-10 h-10 rounded-full bg-white/85 flex items-center justify-center shadow-lg">
                        <div className="w-0 h-0 border-l-[12px] border-l-slate-900 border-y-[8px] border-y-transparent ml-1"></div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="p-2">
                <p className="text-xs font-semibold truncate text-slate-900 dark:text-slate-100">
                  {c.product_name || 'Untitled campaign'}
                </p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  {c.ad_count} ad{c.ad_count === 1 ? '' : 's'} · {c.status || 'draft'}
                </p>
              </div>
            </button>
            {/* Un-favorite button: filled yellow star; tap removes from strip. */}
            <button
              type="button"
              onClick={(e) => handleUnfavorite(e, c.id)}
              disabled={removingId === c.id}
              data-testid={`dashboard-favorite-unfav-${c.id.slice(0,8)}`}
              title="Remove from favorites"
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-yellow-400 text-white flex items-center justify-center shadow hover:bg-yellow-500 hover:scale-110 transition-transform disabled:opacity-50"
            >
              <Star className="w-3.5 h-3.5 fill-current" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FavoriteCampaignsStrip;
